import { ArrowLeftRight, Building2, ChevronDown, LogOut } from 'lucide-react';
import { type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from '@/components/ui';
import { useT } from '@/i18n';
import { WEB_ROLES } from '@/lib/api/types';
import { useApp } from '@/lib/auth/app-context';
import { signOut, switchMembership } from '@/lib/auth/session';
import { cn } from '@/lib/cn';
import { formatPhone } from '@/lib/format/phone';
import { queryClient } from '@/lib/query';

export function UserMenu() {
  const t = useT();
  const app = useApp();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    rootRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const officeMemberships = app.memberships.filter((m) => WEB_ROLES.includes(m.role));
  const canSwitch = officeMemberships.length > 1 || (app.isPlatformAdmin && officeMemberships.length > 0);
  const roleLabel = app.role ? t(`role.${app.role}`) : t('role.PLATFORM');
  const initial = app.user.name.trim().charAt(0);

  const toConsole = async () => {
    setOpen(false);
    try {
      await switchMembership(null);
      queryClient.clear();
      navigate('/platform');
    } catch (e) {
      toast.error(e);
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((o) => !o)}
        className="flex h-11 items-center gap-2.5 rounded-md ps-1 pe-2 transition-colors hover:bg-track"
      >
        <span aria-hidden className="grid size-9 place-items-center rounded-full bg-line text-sm font-semibold">
          {initial}
        </span>
        <span className="hidden min-w-0 flex-col text-start md:flex">
          <span className="max-w-40 truncate text-sm font-medium">{app.user.name}</span>
          <span className="text-xs text-subtle">{roleLabel}</span>
        </span>
        <ChevronDown aria-hidden className="size-4 text-subtle" />
      </button>
      {open ? (
        <div id={menuId} role="menu" className="absolute end-0 top-full z-30 mt-2 w-72 rounded-lg border border-line bg-surface p-1.5 shadow-pop">
          <div className="flex flex-col px-3 py-2.5">
            <span className="truncate font-medium">{app.user.name}</span>
            <span className="text-sm text-subtle">
              {roleLabel}
              {app.school ? ` · ${app.school.name}` : ''}
            </span>
            <bdi dir="ltr" className="text-sm text-subtle">
              {formatPhone(app.user.phone)}
            </bdi>
          </div>
          <div className="my-1 border-t border-line-soft" />
          {canSwitch ? (
            <MenuItem
              icon={<ArrowLeftRight className="size-[18px]" />}
              onClick={() => {
                setOpen(false);
                navigate('/choose');
              }}
            >
              {t('shell.switchSchool')}
            </MenuItem>
          ) : null}
          {app.isPlatformAdmin && app.school ? (
            <MenuItem icon={<Building2 className="size-[18px]" />} onClick={() => void toConsole()}>
              {t('shell.platformConsole')}
            </MenuItem>
          ) : null}
          <MenuItem
            icon={<LogOut className="size-[18px] rtl:-scale-x-100" />}
            onClick={() => {
              setOpen(false);
              void signOut().then(() => queryClient.clear());
            }}
          >
            {t('shell.signOut')}
          </MenuItem>
        </div>
      ) : null}
    </div>
  );
}

function MenuItem({ icon, children, onClick, className }: { icon: ReactNode; children: ReactNode; onClick: () => void; className?: string }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={cn('flex h-11 w-full items-center gap-3 rounded-md px-3 text-start text-base hover:bg-hover focus-visible:bg-hover', className)}
    >
      <span aria-hidden className="text-subtle">
        {icon}
      </span>
      {children}
    </button>
  );
}
